import React, { createContext, useContext, useState, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import ErrorNotification from '../components/ErrorNotification';

interface ErrorContextType {
  showError: (message: string, title?: string, duration?: number) => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

interface Error {
  id: string;
  message: string;
  title: string;
  duration: number;
}

export const ErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [errors, setErrors] = useState<Error[]>([]);

  const showError = useCallback((message: string, title: string = 'Error', duration: number = 5000) => {
    const id = Math.random().toString(36).substr(2, 9);
    setErrors(prev => [...prev, { id, message, title, duration }]);
  }, []);

  const removeError = useCallback((id: string) => {
    setErrors(prev => prev.filter(error => error.id !== id));
  }, []);

  return (
    <ErrorContext.Provider value={{ showError }}>
      {children}
      <AnimatePresence>
        {errors.map(error => (
          <ErrorNotification
            key={error.id}
            message={error.message}
            title={error.title}
            duration={error.duration}
            onClose={() => removeError(error.id)}
          />
        ))}
      </AnimatePresence>
    </ErrorContext.Provider>
  );
};

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
}; 